/*!
 * Created by netlogix GmbH & Co. KG.
 *
 * @copyright netlogix GmbH & Co. KG
 */

import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { isAuthenticated } from '@auth/store/auth.selectors';
import { State } from '@core/store';
import { Actions, createEffect, ofType, ROOT_EFFECTS_INIT } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ReminderOverlayComponent } from '@reminders/reminder-overlay/reminder-overlay.component';
import { ReminderService } from '@reminders/services/reminder.service';
import * as ReminderActions from '@reminders/store/reminder.actions';
import { SuspendReminder } from '@reminders/store/reminder.actions';
import { getMyReminders, getReminderTimerIds, getSuspendedReminderIds } from '@reminders/store/reminders.selectors';
import { ObjectIdPipe } from '@shared/pipes/object-id.pipe';
import { interval, of } from 'rxjs';
import { catchError, concatMap, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

@Injectable()
export class RemindersEffects {
  constructor(
    private actions$: Actions<ReminderActions.ReminderActions>,
    private store: Store<State>,
    private reminderService: ReminderService,
    private dialogService: MatDialog,
    private translationService: TranslateService,
    private snackBarService: MatSnackBar,
    private objectIdPipe: ObjectIdPipe
  ) { }

  
  pollReminders$ = createEffect(() => interval(1000 * 60).pipe(
    switchMap(() => of(new ReminderActions.LoadMyReminders()))
  ));

  
  loadMyReminders$ = createEffect(() => this.actions$.pipe(
    ofType(ROOT_EFFECTS_INIT, ReminderActions.LOAD_MY_REMINDERS),
    switchMap(() => this.store.select(isAuthenticated)),
    filter(authenticated => !!authenticated),
    switchMap(() => {
      const today = new Date();
      const tomorrow = new Date();
      tomorrow.setDate(today.getDate() + 1);

      return this.reminderService.getMyReminders(today, tomorrow);
    }),
    map(reminders => new ReminderActions.LoadMyRemindersSuccess(reminders)),
    catchError(() => of(new ReminderActions.LoadMyRemindersFailure()))
  ));

  
  scheduleReminderTimers$ = createEffect(() => this.actions$.pipe(
    ofType(ReminderActions.LOAD_MY_REMINDERS_SUCCESS),
    withLatestFrom(this.store.select(getReminderTimerIds)),
    tap(([_, timerIds]) => timerIds.forEach(id => window.clearTimeout(id))),
    concatMap(() => this.store.select(getMyReminders).pipe(
      withLatestFrom(this.store.select(getSuspendedReminderIds)),
      map(([reminders, suspendedReminderIds]) => reminders
        .filter(reminder => !suspendedReminderIds.includes(this.objectIdPipe.transform(reminder)))
        .map(reminder => {
          const millisecondsTillNotification = new Date(reminder.dateTime).valueOf() - new Date().valueOf();
          return window.setTimeout(
            () => this.store.dispatch(new ReminderActions.ShowReminderOverlay(reminder)),
            millisecondsTillNotification
          );
      })),
      map(timerIds => new ReminderActions.SetReminderTimers(timerIds))
    ))
  ));

  
  showReminder$ = createEffect(() => this.actions$.pipe(
    ofType(ReminderActions.SHOW_REMINDER_OVERLAY),
    tap(action => this.dialogService.open(ReminderOverlayComponent, {
      data: {
        reminder: action.reminder
      }
    })),
    map(action => new SuspendReminder(this.objectIdPipe.transform(action.reminder)))
  ));

  
  setReminderInactive$ = createEffect(() => this.actions$.pipe(
    ofType(ReminderActions.SET_REMINDER_INACTIVE),
    switchMap(action => this.reminderService.updateReminder({
      '@id': action.reminderId.startsWith('/api/reminders/')
        ? action.reminderId
        : '/api/reminders/' + action.reminderId,
      active: false
    }).pipe(
      map(() => new ReminderActions.SetReminderInactiveSuccess()),
      catchError(() => of(new ReminderActions.SetReminderInactiveFailure()))
    ))
  ));

  
  setReminderInactiveFailure$ = createEffect(() => this.actions$.pipe(
    ofType(ReminderActions.SET_REMINDER_INACTIVE_FAILURE),
    switchMap(() => this.translationService.get('reminders.errors.setInactive')),
    tap(errorMessage => this.snackBarService.open(errorMessage))
  ), { dispatch: false });
}
